import apiClient from '@disco/api/server'
import type { ExternalIdentifier } from 'types/writer'

import { api } from './index'

export interface Writer {
  id: number
  name: string
  pros: number[] | null | undefined
  publishers: number[] | null | undefined
  default_pro: number | null | undefined
  default_publisher: number | null | undefined
  external_identifiers?: ExternalIdentifier[]
}

interface SearchWritersResponse {
  results: Writer[]
}

interface SearchWritersParams {
  query: string
  limit?: number
  exclusionList?: number[]
}

/**
 * Writers API endpoints
 *
 * Provides RTK Query endpoints for searching and managing writers.
 * Uses code splitting pattern with enhanceEndpoints and injectEndpoints.
 */
export const writersApi = api
  .enhanceEndpoints({ addTagTypes: ['Writers'] })
  .injectEndpoints({
    endpoints: build => ({
      searchWriters: build.query<Writer[], SearchWritersParams>({
        // Only use query and limit for cache key - exclusionList is filtered client-side
        serializeQueryArgs: ({ queryArgs }) => {
          return { query: queryArgs.query, limit: queryArgs.limit ?? 20 }
        },
        queryFn: async ({ query, limit = 20 }) => {
          try {
            const response: SearchWritersResponse = await apiClient.getWriters(
              query,
              limit,
            )

            return { data: response.results }
          } catch (error) {
            return { error }
          }
        },
        providesTags: ['Writers'],
        // Cache individual writer records from search results
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            // Populate getWriterById cache with each writer from the search results
            data.forEach(writer => {
              dispatch(
                writersApi.util.upsertQueryData(
                  'getWriterById',
                  writer.id.toString(),
                  writer,
                ),
              )
            })
          } catch {
            // If search fails, don't populate cache
          }
        },
      }),
      getWriterById: build.query<Writer | null, string | number>({
        queryFn: async writerId => {
          try {
            // Fetch individual writer by ID using the contact API
            const writer = await apiClient.getSingleContact(Number(writerId))

            return { data: writer || null }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, writerId) => [
          { type: 'Writers', id: writerId },
        ],
      }),
    }),
  })

export const { useSearchWritersQuery, useGetWriterByIdQuery } = writersApi
